import { FlexProps, chakra } from '@chakra-ui/react';
import { FC } from 'react';
import { MdInfo } from 'react-icons/md';
import {
  QUOTE_FIELD_CONTROL_ATTRIBUTE,
  QuoteFieldAnchorKey,
} from '../../utils/quoteFieldValidations';
import {
  MBox,
  MButton,
  MCustomIconButton,
  MFlex,
  MText,
  MTooltip,
} from '../Monetize';
import { QuoteFieldAnchor } from './QuoteFieldAnchor';

interface QuoteBasicInfoLineProps extends FlexProps {
  title: string;
  children?: React.ReactElement<any> | string;
  inputId?: string;
  showRequired?: boolean;
  orientation?: 'horizontal' | 'vertical';
  labelExtra?: React.ReactElement;
  /**
   * Turns the title into a button instead of a form label. Use when activating
   * the title opens something (e.g. a settings drawer) rather than focusing the
   * row's control — a `<label>` must only ever activate the input it labels, so
   * rows using this must give their control its own accessible name.
   */
  onTitleClick?: () => void;
  /**
   * With `onTitleClick`, explains what it does, on hover and on keyboard
   * focus. Without it, renders as a standalone info-icon tooltip next to
   * the label.
   */
  titleTooltip?: string;
  /**
   * Opts the row into inline rule validations. Typed against the anchor
   * registry so a key here cannot drift from QUOTE_FIELD_ANCHOR_KEYS.
   */
  fieldKey?: QuoteFieldAnchorKey | readonly QuoteFieldAnchorKey[];
}

export const QuoteBasicInfoLine: FC<QuoteBasicInfoLineProps> = ({
  title,
  children,
  inputId,
  showRequired,
  orientation = 'horizontal',
  labelExtra,
  onTitleClick,
  titleTooltip,
  fieldKey,
  ...rest
}: QuoteBasicInfoLineProps) => {
  const titleContent = (
    <>
      {title}
      {showRequired && (
        <MText as="span" color="tRed.base" ml="1">
          *
        </MText>
      )}
    </>
  );

  const withFieldAnchor = (row: React.ReactElement) =>
    fieldKey ? (
      <QuoteFieldAnchor fieldKey={fieldKey}>{row}</QuoteFieldAnchor>
    ) : (
      row
    );

  if (orientation === 'horizontal') {
    return withFieldAnchor(
      <MFlex
        alignItems="center"
        justifyContent="space-between"
        minHeight="8"
        my="1"
        {...rest}
      >
        {onTitleClick ? (
          <MTooltip label={titleTooltip} placement="top-start">
            <MButton
              type="button"
              variant="link"
              onClick={onTitleClick}
              mr="6"
              whiteSpace="nowrap"
              // The title and the required star are text, not an icon: v3's
              // button gap (8px) would push the star well past the 4px it
              // sits from a plain label title.
              gap={0}
            >
              {titleContent}
            </MButton>
          </MTooltip>
        ) : (
          <MFlex alignItems="center" mr="6">
            <chakra.label
              m="0"
              color="tGray.darkPurple"
              htmlFor={inputId}
              fontWeight="400"
              css={{ textWrap: 'nowrap' }}
            >
              {titleContent}
            </chakra.label>
            {titleTooltip && (
              <MTooltip label={titleTooltip} placement="top-start">
                <MCustomIconButton
                  aria-label={`${title} information`}
                  icon={MdInfo}
                  iconColor="tPurple.base"
                  ml="1"
                />
              </MTooltip>
            )}
          </MFlex>
        )}

        <MBox
          {...{ [QUOTE_FIELD_CONTROL_ATTRIBUTE]: '' }}
          minWidth={0}
          css={{
            // Mixed font weights between input-driven values (400) and
            // button-driven popovers (500) read as inconsistent in this
            // section. Pin everything to 500 so the row reads uniformly.
            '& p, & span, & button, & input': {
              fontWeight: '500 !important',
            },
          }}
        >
          {children}
        </MBox>
      </MFlex>,
    );
  }

  return withFieldAnchor(
    <MFlex
      alignItems="flex-start"
      justifyContent="space-between"
      flexDir="column"
      {...rest}
    >
      <MFlex alignItems="center">
        <chakra.label
          m="0"
          mr="2"
          color="tPurple.dark"
          htmlFor={inputId}
          fontWeight="600"
          css={{ textWrap: 'nowrap' }}
        >
          {titleContent}
        </chakra.label>

        {labelExtra}
      </MFlex>

      <MFlex
        {...{ [QUOTE_FIELD_CONTROL_ATTRIBUTE]: '' }}
        minWidth={0}
        mt="1"
        minHeight="8"
        alignItems="center"
      >
        {children}
      </MFlex>
    </MFlex>,
  );
};
